import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import { URLS } from '../../config';
import { components } from '../../components';
import { theme } from '../../constants';

export const Blog: React.FC = () => {
  const navigate = useNavigate();
  const [articles, setArticles] = useState<any[]>([]);
  const [filteredArticles, setFilteredArticles] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [visibleCount, setVisibleCount] = useState(18);

  const loadMoreRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetch(URLS.GET_DATA)
      .then((res) => res.json())
      .then((data) => {
        setArticles(data.issues || []);
        setFilteredArticles(data.issues || []);
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    const filtered = articles.filter(article =>
      article.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      article.description.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredArticles(filtered);
    setVisibleCount(18);
  }, [searchTerm, articles]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && visibleCount < filteredArticles.length) {
          setVisibleCount((prev) => prev + 18);
        }
      },
      { threshold: 1 }
    );

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    return () => {
      if (loadMoreRef.current) {
        observer.unobserve(loadMoreRef.current);
      }
    };
  }, [filteredArticles, visibleCount]);

  const renderHeader = (): JSX.Element => {
    return (
      
      <components.Header
      
        burger={true}
        basket={true}
        line={true}
      />
    );
  };

  const renderSearch = (): JSX.Element => (
    <div style={{ padding: '10px 20px' }}>
      <input
        type="text"
        placeholder="Search articles..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        style={{
          width: '100%',
          padding: '10px',
          borderRadius: '6px',
          border: '1px solid #ccc',
          fontSize: '16px',
        }}
      />
    </div>
  );
  const renderNoResults = (): JSX.Element => (
  <div
    style={{
      padding: '60px 20px',
      textAlign: 'center',
      fontSize: '18px',
     
      backgroundColor: '#f9f9f9',
      borderRadius: '10px',
      margin: '20px',
      boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
    }}
  >
    <p style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '10px' }}>
       No Results Found
    </p>
    <p>
      We couldn’t find any articles matching "
      <span style={{ color: theme.colors.mainColor, fontWeight: '600' }}>
        {searchTerm}
      </span>
      ".
    </p>
    <p style={{ fontSize: '16px', marginTop: '10px' }}>
      Try adjusting your search term or browsing other sections.
    </p>
  </div>
);


  const renderSkeletons = () => (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: '5px',
        padding: '5px',
      }}
    >
      {[...Array(6)].map((_, i) => (
        <div
          key={i}
          style={{
            height: '300px',
            backgroundColor: '#f0f0f0',
            borderRadius: '10px',
            animation: 'pulse 1.5s infinite',
          }}
        />
      ))}
    </div>
  );

  const renderArticles = (): JSX.Element => {
    const visibleArticles = filteredArticles.slice(0, visibleCount);

    return (
      <div
    style={{
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '20px',
    padding: '20px',
  }}
      >
        {visibleArticles.map((article, index) => (
          <div
            key={index}
            onClick={() => navigate(`/article/${article.id}`)}
            style={{
              cursor: 'pointer',
              backgroundColor: '#fff',
              borderRadius: '10px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              transition: 'transform 0.3s ease, box-shadow 0.3s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-5px)';
              e.currentTarget.style.boxShadow = '0 10px 20px rgba(0, 0, 0, 0.15)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
            }}
          >
            <img
              src={article.image}
              alt={article.title}
              style={{
                width: '100%',
                height: '180px',
                objectFit: 'cover',
              }}
            />
            <div style={{ padding: '15px' }}>

            <h3
  style={{
    fontSize: '18px',
    fontWeight: 700,
    margin: 0,
    marginBottom: '8px',
    color: theme.colors.mainColor,
    lineHeight: 1.3,
    letterSpacing: 0.2,
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
  }}
>
  {article.title}
</h3>
<p
  style={{
    fontSize: '14px',
    color: '#555',
    margin: 0,
    lineHeight: 1.5,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    minHeight: '42px', // ensures space for 2 lines
  }}
>
  {article.description}
</p>

            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderLoadMore = (): JSX.Element => (
    <div ref={loadMoreRef} style={{ height: '40px' }} />
  );

  const renderBottomTabBar = (): JSX.Element => {
    return (
      <div style={{ height: '80px', width: '100%' }}>
        <components.BottomTabBar />
      </div>
    );
  };

  return (
    <>
      {renderHeader()}
      {renderSearch()}
    {loading ? (
  renderSkeletons()
) : filteredArticles.length === 0 ? (
  renderNoResults()
) : (
  renderArticles()
)}

      {/* RENDER loadMoreRef only if we aren't loading AND more articles exist */}
      {!loading && visibleCount < filteredArticles.length && renderLoadMore()}
      {renderBottomTabBar()}
    </>
  );
};
